import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ViewCredentialsModal = ({ isOpen, onClose, institute }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(null);

  if (!isOpen || !institute) return null;

  const email = institute?.user?.email || '—';
  const password = institute?.user?.plain_password || null;
  const instituteName = institute?.instituteName || 'Institute';

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // fallback
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-gray-50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="KeyRound" size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Login Credentials</h2>
              <p className="text-xs text-muted-foreground">{instituteName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-200 transition-colors text-muted-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Login URL */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs font-medium text-blue-700 mb-1">Login URL</p>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-blue-900 font-mono break-all">
                https://studyexpo.online/institute-login
              </span>
              <button
                onClick={() => handleCopy('https://studyexpo.online/institute-login', 'url')}
                className="flex-shrink-0 p-1.5 rounded hover:bg-blue-100 transition-colors"
                title="Copy URL"
              >
                <Icon name={copied === 'url' ? 'Check' : 'Copy'} size={14} className={copied === 'url' ? 'text-green-600' : 'text-blue-600'} />
              </button>
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Login Email</label>
            <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-border rounded-lg">
              <Icon name="Mail" size={14} className="text-muted-foreground flex-shrink-0" />
              <span className="flex-1 text-sm text-foreground font-mono break-all">{email}</span>
              <button
                onClick={() => handleCopy(email, 'email')}
                className="flex-shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
                title="Copy email"
              >
                <Icon name={copied === 'email' ? 'Check' : 'Copy'} size={13} className={copied === 'email' ? 'text-green-600' : 'text-muted-foreground'} />
              </button>
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Password</label>
            {password ? (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-border rounded-lg">
                <Icon name="Lock" size={14} className="text-muted-foreground flex-shrink-0" />
                <span className="flex-1 text-sm text-foreground font-mono tracking-wider break-all">
                  {showPassword ? password : '•'?.repeat(Math.min(password?.length, 12))}
                </span>
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="flex-shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={13} className="text-muted-foreground" />
                </button>
                <button
                  onClick={() => handleCopy(password, 'password')}
                  className="flex-shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
                  title="Copy password"
                >
                  <Icon name={copied === 'password' ? 'Check' : 'Copy'} size={13} className={copied === 'password' ? 'text-green-600' : 'text-muted-foreground'} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <Icon name="AlertTriangle" size={14} className="text-amber-600 flex-shrink-0" />
                <span className="text-sm text-amber-700">
                  Password not available. This institute was created before credential storage was enabled, or was self-registered.
                </span>
              </div>
            )}
          </div>

          {/* Note */}
          <p className="text-xs text-muted-foreground bg-gray-50 rounded-lg px-3 py-2 border border-border">
            <Icon name="Info" size={12} className="inline mr-1 text-muted-foreground" />
            Share these credentials with the institute via your personal email. Advise them to change their password after first login.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewCredentialsModal;
